import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Search, TrendingUp, TrendingDown, Wallet, Target, AlertTriangle, Settings2, Building2, ChevronRight } from "lucide-react";
import { ProjectPnlDetail } from "./pnl/ProjectPnlDetail";
import { OverheadsDialog } from "./pnl/OverheadsDialog";
import { PnlSettingsDialog } from "./pnl/PnlSettingsDialog";
import { eur, pct, hrs, STATUS, currentYearStart } from "./pnl/format";

const RO_MONTHS_SHORT = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Burn({ value, budget, color }) {
  if (!budget) return <span className="text-[11px] text-slate-300">fără buget</span>;
  const p = value / budget * 100;
  const c = p > 100 ? "#dc2626" : p >= 90 ? "#d97706" : color;
  return (
    <div className="min-w-[110px]">
      <div className="text-[11px] font-semibold tabular-nums" style={{ color: c }}>{pct(p)}</div>
      <div className="pio-progress mt-1"><div style={{ width: `${Math.min(100, p)}%`, background: c }} /></div>
    </div>
  );
}

function Spark({ rows }) {
  const w = 72, h = 22;
  const max = Math.max(1, ...rows.map((r) => Math.max(r.budget, r.planned_value, r.actual_value)));
  const n = rows.length || 1;
  const bw = w / n;
  return (
    <svg width={w} height={h} className="block" aria-hidden>
      {rows.map((r, i) => (
        <g key={r.month}>
          <rect x={i * bw} y={h - (r.budget / max) * h} width={bw * 0.9} height={(r.budget / max) * h} fill="#e2e8f0" />
          <rect x={i * bw} y={h - (r.planned_value / max) * h} width={bw * 0.45} height={(r.planned_value / max) * h} fill="#3d68b1" />
          <rect x={i * bw + bw * 0.45} y={h - (r.actual_value / max) * h} width={bw * 0.45} height={(r.actual_value / max) * h} fill="#16a34a" />
        </g>
      ))}
    </svg>
  );
}

export default function Budgets() {
  const { hasPermission, user } = useAuth();
  const canEdit = user?.role === "admin" || hasPermission("finance.manage") || hasPermission("projects.manage");
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [fromMonth, setFromMonth] = useState(currentYearStart());
  const [months, setMonths] = useState(12);
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [overheadsOpen, setOverheadsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const selected = params.get("project");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        api.get("/pnl/overview", { params: { from_month: fromMonth, months, include_completed: includeCompleted } }),
        api.get("/pnl/settings"),
      ]);
      setData(o.data); setSettings(s.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [fromMonth, months, includeCompleted]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let r = data?.projects || [];
    if (onlyActive) r = r.filter((x) => x.totals.planned_value > 0 || x.totals.actual_value > 0 || x.budget_total > 0);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((x) => x.project_name.toLowerCase().includes(s) || (x.project_code || "").toLowerCase().includes(s) || (x.client_name || "").toLowerCase().includes(s));
    }
    return r;
  }, [data, q, onlyActive]);

  const kpi = useMemo(() => {
    const t = { budget: 0, planned: 0, actual: 0, cost: 0, margin: 0, over: 0, warn: 0 };
    for (const r of rows) {
      t.budget += r.budget_total; t.planned += r.totals.planned_value; t.actual += r.totals.actual_value;
      t.cost += r.totals.total_cost; t.margin += r.totals.margin;
      if (r.totals.status === "over") t.over += 1;
      if (r.totals.status === "warning") t.warn += 1;
    }
    return t;
  }, [rows]);

  const openProject = (id) => { const n = new URLSearchParams(params); if (id) n.set("project", id); else n.delete("project"); setParams(n, { replace: true }); };
  const year = parseInt(fromMonth.slice(0, 4), 10), mon = parseInt(fromMonth.slice(5, 7), 10);
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <PortalLayout variant="staff" wide>
      <div data-testid="pnl-page">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-5 gap-3">
          <div>
            <div className="section-eyebrow">Proiecte</div>
            <h1 className="section-title">Profit & Loss</h1>
            <p className="text-[12px] text-slate-400 mt-1">Buget pe perioade vs. valoarea alocărilor (plan) și a orelor pontate (real), cu manoperă, cheltuieli directe, indirecte și regie — în EUR.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="pnl-from-month" value={mon} onChange={(e) => setFromMonth(`${year}-${String(e.target.value).padStart(2, "0")}`)} className="pio-input">
              {RO_MONTHS_SHORT.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select data-testid="pnl-from-year" value={year} onChange={(e) => setFromMonth(`${e.target.value}-${String(mon).padStart(2, "0")}`)} className="pio-input">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select data-testid="pnl-months" value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10))} className="pio-input">
              {[6, 12, 18, 24].map((n) => <option key={n} value={n}>{n} luni</option>)}
            </select>
            <button data-testid="pnl-open-overheads" onClick={() => setOverheadsOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium bg-white border border-slate-200 hover:border-slate-300 text-slate-700">
              <Building2 className="h-3.5 w-3.5" /> Regie lunară</button>
            <button data-testid="pnl-open-settings" onClick={() => setSettingsOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium bg-white border border-slate-200 hover:border-slate-300 text-slate-700">
              <Settings2 className="h-3.5 w-3.5" /> Setări</button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
            <input data-testid="pnl-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută proiect sau client..." className="pio-input pr-8 w-64" />
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} data-testid="pnl-only-active" className="accent-[#3d68b1]" />
            Doar cu buget sau activitate
          </label>
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} data-testid="pnl-include-completed" className="accent-[#3d68b1]" />
            Include proiecte finalizate
          </label>
          {settings && <span className="text-[11px] text-slate-400 ml-auto">1 € = {settings.eur_ron_rate} RON · contribuții {settings.employer_contrib_pct}% · indirecte implicite {settings.indirect_pct_default}%</span>}
        </div>

        {loading && !data ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
              <KpiCard testId="pnl-kpi-budget" icon={Target} label="Buget total" value={eur(kpi.budget)} accent="#334155" />
              <KpiCard testId="pnl-kpi-planned" icon={Wallet} label="Planificat (alocări)" value={eur(kpi.planned)} sub={kpi.budget ? `${pct(kpi.planned / kpi.budget * 100)} din buget` : ""} accent="#3d68b1" />
              <KpiCard testId="pnl-kpi-actual" icon={Wallet} label="Realizat (pontaje)" value={eur(kpi.actual)} sub={kpi.budget ? `${pct(kpi.actual / kpi.budget * 100)} din buget` : ""} accent="#16a34a" />
              <KpiCard testId="pnl-kpi-cost" icon={Wallet} label="Cost total" value={eur(kpi.cost)} sub="manoperă + directe + indirecte + regie" accent="#8B6E4A" />
              <KpiCard testId="pnl-kpi-margin" icon={kpi.margin >= 0 ? TrendingUp : TrendingDown} label="Marjă reală" value={eur(kpi.margin)} sub={kpi.actual ? pct(kpi.margin / kpi.actual * 100) : ""} accent={kpi.margin >= 0 ? "#16a34a" : "#dc2626"} />
              <KpiCard testId="pnl-kpi-over" icon={AlertTriangle} label="Peste buget" value={`${kpi.over} / ${rows.length}`} sub={kpi.warn ? `+${kpi.warn} aproape de limită` : "proiecte"} accent={kpi.over > 0 ? "#dc2626" : "#16a34a"} />
            </div>

            <div className="surface overflow-hidden">
              <div className="max-h-[calc(100vh-380px)] overflow-y-auto overflow-x-auto">
                <table className="pio-table">
                  <thead>
                    <tr>
                      <th className="w-[22%]">Proiect</th>
                      <th className="text-right">Buget</th>
                      <th>Planificat (alocări)</th>
                      <th>Realizat (pontaje)</th>
                      <th className="text-right">Rămas vs. plan</th>
                      <th className="text-right">Cost total</th>
                      <th className="text-right">Marjă reală</th>
                      <th>Status</th>
                      <th>Evoluție</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && <tr><td colSpan={10} className="py-12 text-center text-slate-400">Niciun proiect găsit.</td></tr>}
                    {rows.map((r) => {
                      const s = STATUS[r.totals.status] || STATUS.no_budget;
                      return (
                        <tr key={r.project_id} data-testid={`pnl-row-${r.project_id}`} className="cursor-pointer" onClick={() => openProject(r.project_id)}>
                          <td>
                            <div className="font-semibold text-[13px] text-slate-800 leading-tight">{r.project_name}</div>
                            <div className="text-[11px] text-slate-400">{r.project_code}{r.client_name ? ` · ${r.client_name}` : ""}{r.has_periods ? " · buget pe perioade" : ""}</div>
                          </td>
                          <td className="text-right tabular-nums text-[13px] font-semibold text-slate-700">{r.budget_total ? eur(r.budget_total) : <span className="text-slate-300">—</span>}</td>
                          <td>
                            <div className="text-[12px] tabular-nums text-[#3d68b1] font-semibold">{eur(r.totals.planned_value)} <span className="text-slate-400 font-normal">· {hrs(r.totals.planned_hours)}</span></div>
                            <Burn value={r.totals.planned_value} budget={r.budget_total} color="#3d68b1" />
                          </td>
                          <td>
                            <div className="text-[12px] tabular-nums text-green-700 font-semibold">{eur(r.totals.actual_value)} <span className="text-slate-400 font-normal">· {hrs(r.totals.actual_hours)}</span></div>
                            <Burn value={r.totals.actual_value} budget={r.budget_total} color="#16a34a" />
                          </td>
                          <td className={`text-right tabular-nums text-[13px] font-semibold ${r.totals.budget_remaining_planned < 0 ? "text-red-600" : "text-slate-700"}`}>
                            {r.budget_total ? eur(r.totals.budget_remaining_planned) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="text-right tabular-nums text-[13px] text-slate-600">{eur(r.totals.total_cost)}</td>
                          <td className="text-right">
                            <div className={`tabular-nums text-[13px] font-semibold ${r.totals.margin >= 0 ? "text-green-700" : "text-red-600"}`}>{eur(r.totals.margin)}</div>
                            {r.totals.margin_pct != null && <div className={`text-[11px] tabular-nums ${r.totals.margin >= 0 ? "text-green-600" : "text-red-500"}`}>{pct(r.totals.margin_pct)}</div>}
                          </td>
                          <td><span className={`pio-badge ${s.badge}`} data-testid={`pnl-status-${r.project_id}`}>{s.label}</span></td>
                          <td><Spark rows={r.rows} /></td>
                          <td className="text-right"><ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-[11px] text-slate-400 mt-2 flex gap-4 flex-wrap">
              <span><span className="inline-block h-2 w-3 bg-slate-200 mr-1 align-middle" />buget lunar</span>
              <span><span className="inline-block h-2 w-3 bg-[#3d68b1] mr-1 align-middle" />plan (alocări × fee rate)</span>
              <span><span className="inline-block h-2 w-3 bg-green-600 mr-1 align-middle" />realizat (pontaje × fee rate)</span>
              <span>Click pe un proiect pentru evoluția lunară, echipă și cheltuieli.</span>
            </div>
          </>
        )}

        {selected && (
          <ProjectPnlDetail projectId={selected} fromMonth={fromMonth} months={months} canEdit={canEdit}
            settings={settings} onClose={() => openProject(null)} onChanged={load} />
        )}
        <OverheadsDialog open={overheadsOpen} onClose={() => setOverheadsOpen(false)} canEdit={canEdit} onChanged={load} />
        <PnlSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={settings} canEdit={canEdit}
          onSaved={(s) => { setSettings({ ...settings, ...s }); load(); }} />
      </div>
    </PortalLayout>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div className="surface p-3.5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="h-6 w-6 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, color: accent }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      </div>
      <div className="text-[17px] font-bold tabular-nums text-slate-800 font-display">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
